import { get } from './get'

export const normalizeString = (value) => {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

export function normalizeDiacritics (str) {
  if (!str) return ''

  return str
    .normalize('NFKD')
    .replace(/[\u0080-\uF8FF]/g, '')
    .toLowerCase()
}

export const getEntityQuery = (entity, props) => {
  const entityObj = entity || {}
  return props.map((prop) => {
    const value = (get(entityObj, prop) || '').toLowerCase()
    return value
  }).join(' ')
}

export const matches = (word, words) => {
  if (word) {
    const noAccentsWords = normalizeString(words)
    const regularSplit = words.split(word).length - 1
    const noAccentsSplit = noAccentsWords.split(word).length - 1
    return regularSplit || noAccentsSplit
  }

  return 0
}

// Very simple string formating function, for now
// works mainly to format numbers or string of numbers
// to a given format, we're using the pound (#) sign here
// to define the placeholders in the format string.
export const format = (string, format) => (string || '').split('')
  .reduce((acc, char) => acc.replace(/#/, char), format)
  .replace(/\D?#/g, '')

export const capitalize = (string) => {
  return string.replace(/(?:^|\s)\S/g, a => a.toUpperCase())
}

// Removes HTML tags from string
export const sanitize = string => (string || '')
  .replace(/<\/?[^>]+(>|$)/g, '')
  .trim()

export const getMatches = (word = '', words = '') => {
  const normalizedWord = normalizeString(word)
  const normalizedWords = normalizeString(words)

  return normalizedWords
    .toLowerCase()
    .split(normalizedWord.toLowerCase())
}

export const getRemaining = (word, words) => {
  const matches = getMatches(word, words)
  const atBeginning = !(matches[0] || []).length
  const remaining = (matches.filter(Boolean)[0] || []).length

  return {
    atBeginning,
    remaining: atBeginning ? remaining * -1 : remaining
  }
}

export const getDiacritic = (obj, key, word, words) => {
  const { atBeginning, remaining } = getRemaining(word, words)
  const untilEnd = remaining + word.length

  if (word === words) return obj[key]

  return atBeginning
    ? obj[key].slice(0, remaining)
    : obj[key].slice(remaining, untilEnd)
}

export const setDiacritic = (xs, normalizeProp, searchProp) => xs.map(item => ({
  ...item,
  [normalizeProp]: normalizeString(item[searchProp])
}))

/**
 * Receives an array of strings and outputs it in a single string
 * with the following pattern:
 *
 * ['A', 'B', 'C', 'D' ] => A, B, C e D
 * @param {Array<string>} arr
 * @returns {string}
 */
export const stringifyList = (arr = []) => {
  if (arr.length === 1) return arr[0]

  const firsts = arr.slice(0, arr.length - 1)
  const last = arr[arr.length - 1]

  return firsts.join(', ') + ' e ' + last
}

// transpiles a snake_case property name to camelCase
// (including default mongodb '_id' property to 'id')
export const snakeToCamel = s =>
  s.replace(/([-_][a-z])/ig, v => v.toUpperCase().replace('-', '').replace('_', ''))

// transpiles snakeCase  object properties to  camel_case (it goes deep into objects and arrays)
export const snakeToCamelKeys = o => Object.entries(o).reduce((a, [ p, v ]) => ({ ...a,
  [snakeToCamel(p)]: (v && typeof v === 'object')
    ? Array.isArray(v)
      ? v.map((v) => (v && typeof v === 'object') ? snakeToCamelKeys(v) : v)
      : snakeToCamelKeys(v) : v }), {})

export const camelToSnake = (s) => {
  return s.replace(/[A-Z]/g, (match) => '_' + match.toLowerCase())
}

export const camelToSnakeKeys = o => Object.entries(o).reduce((a, [ p, v ]) => ({
  ...a,
  [camelToSnake(p)]: (v && typeof v === 'object')
    ? Array.isArray(v)
      ? v.map(item => typeof item === 'object' ? camelToSnakeKeys(item) : item)
      : camelToSnakeKeys(v)
    : v
}), {})

/**
 * Converts a given string to kebab-case.
 *
 * This function normalizes the input string, replaces spaces and camelCase
 * patterns with hyphens, and converts the entire string to lowercase.
 *
 * @param {string} string - The input string to be converted.
 * @returns {string} The converted string in kebab-case format.
 */
export const toKebabCase = (string) => {
  return normalizeString(string)
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase()
}

/**
 * Copies a given string to the user's clipboard.
 *
 * This function attempts to use the modern Clipboard API when available.
 * If the API is not supported, it falls back to creating a temporary
 * textarea element to execute the copy command manually.
 *
 * @async
 * @param {string} string - The text content to be copied to the clipboard.
 * @returns {Promise<boolean>} Returns `true` if the text was successfully copied, or `false`.
 */
export const copyToClipboard = async (string) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(string)
      return true
    }

    const textarea = document.createElement('textarea')
    textarea.value = string
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)

    return true
  } catch (_) {
    return false
  }
}

/**
 * Formats a CNPJ string with mask pattern: XX.XXX.XXX/XXXX-XX
 * Supports both numeric and alphanumeric CNPJs (2026+ format)
 * @param {string} cnpj - CNPJ without formatting (14 characters)
 * @returns {string} Formatted CNPJ or original string if invalid
 */
export const formatCnpj = (cnpj) => {
  if (!cnpj || cnpj.length !== 14) return cnpj
  return format(cnpj, '##.###.###/####-##')
}

/**
 * Formats a phone string with mask pattern
 * @param {string} phone - Phone without formatting (only numbers)
 * @returns {string} Formatted phone or original string if invalid
 */
export const formatPhone = (phone) => {
  if (!phone || phone.length < 10) return phone
  const phoneFormat = phone.length === 10 ? '## #### ####' : '## ##### ####'
  return format(phone, phoneFormat)
}
